export interface LoanInput {
  principal: number;
  annualRate: number;
  tenureMonths: number;
  interestType: 'reducing' | 'flat';
  startDate?: Date;
}

export interface EMIResult {
  emi: number;
  totalInterest: number;
  totalPayable: number;
  endDate: Date;
  numberOfEMIs: number;
}

export interface MonthlyBreakdown {
  month: number;
  date: Date;
  openingBalance: number;
  emi: number;
  principal: number;
  interest: number;
  closingBalance: number;
  totalPrincipalPaid: number;
  totalInterestPaid: number;
}

export interface LoanSchedule {
  result: EMIResult;
  schedule: MonthlyBreakdown[];
}

// Calculate EMI using the standard formula for reducing balance
export function calculateReducingEMI(principal: number, annualRate: number, tenureMonths: number): number {
  if (annualRate === 0) return principal / tenureMonths;
  
  const monthlyRate = annualRate / 12 / 100;
  const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths) 
              / (Math.pow(1 + monthlyRate, tenureMonths) - 1);
  
  return Math.round(emi * 100) / 100;
}

// Calculate EMI for flat rate
export function calculateFlatEMI(principal: number, annualRate: number, tenureMonths: number): number {
  const totalInterest = (principal * annualRate * (tenureMonths / 12)) / 100;
  const emi = (principal + totalInterest) / tenureMonths;
  return Math.round(emi * 100) / 100;
}

// Generate complete amortization schedule
export function calculateLoanSchedule(input: LoanInput): LoanSchedule {
  const { principal, annualRate, tenureMonths, interestType, startDate = new Date() } = input;
  
  const emi = interestType === 'reducing' 
    ? calculateReducingEMI(principal, annualRate, tenureMonths)
    : calculateFlatEMI(principal, annualRate, tenureMonths);
  
  const schedule: MonthlyBreakdown[] = [];
  let balance = principal;
  let totalPrincipalPaid = 0;
  let totalInterestPaid = 0;
  
  const monthlyRate = annualRate / 12 / 100;
  const flatMonthlyInterest = interestType === 'flat' 
    ? (principal * annualRate * (tenureMonths / 12)) / 100 / tenureMonths 
    : 0;
  
  for (let month = 1; month <= tenureMonths; month++) {
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + month);
    
    const openingBalance = balance;
    
    let interestPayment: number;
    let principalPayment: number;
    
    if (interestType === 'reducing') {
      interestPayment = balance * monthlyRate;
      principalPayment = emi - interestPayment;
    } else {
      interestPayment = flatMonthlyInterest;
      principalPayment = emi - interestPayment;
    }
    
    // Handle last month rounding
    if (month === tenureMonths) {
      principalPayment = balance;
    }
    
    balance = Math.max(0, balance - principalPayment);
    totalPrincipalPaid += principalPayment;
    totalInterestPaid += interestPayment;
    
    schedule.push({
      month,
      date,
      openingBalance: Math.round(openingBalance * 100) / 100,
      emi: Math.round(emi * 100) / 100,
      principal: Math.round(principalPayment * 100) / 100,
      interest: Math.round(interestPayment * 100) / 100,
      closingBalance: Math.round(balance * 100) / 100,
      totalPrincipalPaid: Math.round(totalPrincipalPaid * 100) / 100,
      totalInterestPaid: Math.round(totalInterestPaid * 100) / 100,
    });
  }
  
  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + tenureMonths);
  
  return {
    result: {
      emi: Math.round(emi * 100) / 100,
      totalInterest: Math.round(totalInterestPaid * 100) / 100,
      totalPayable: Math.round((principal + totalInterestPaid) * 100) / 100,
      endDate,
      numberOfEMIs: tenureMonths,
    },
    schedule,
  };
}

// Format currency using Indian number system
export function formatCurrency(amount: number): string {
  const absAmount = Math.abs(amount);
  const isNegative = amount < 0;
  
  // Round to nearest integer
  const rounded = Math.round(absAmount);
  const intStr = rounded.toString();
  
  // Apply Indian comma formatting
  let formatted: string;
  if (intStr.length <= 3) {
    formatted = intStr;
  } else {
    const lastThree = intStr.slice(-3);
    const remaining = intStr.slice(0, -3);
    const groups: string[] = [];
    let temp = remaining;
    while (temp.length > 0) {
      groups.unshift(temp.slice(-2));
      temp = temp.slice(0, -2);
    }
    formatted = [...groups, lastThree].join(',');
  }
  
  return `${isNegative ? '-' : ''}₹${formatted}`;
}

// Format date - handles both Date objects and date strings from localStorage
export function formatDate(date: Date | string): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
    return new Intl.DateTimeFormat('en-IN', {
      month: 'short',
      year: 'numeric',
    }).format(dateObj);
  } catch {
    return 'N/A';
  }
}

// Loan type presets
export interface LoanPreset {
  id: string;
  name: string;
  icon: string;
  defaultRate: number;
  defaultTenureYears: number;
  minAmount: number;
  maxAmount: number;
  description: string;
}

export const loanPresets: LoanPreset[] = [
  
  {
    id: 'personal',
    name: 'Personal',
    icon: '💳',
    defaultRate: 12,
    defaultTenureYears: 5,
    minAmount: 50000,
    maxAmount: 4000000,
    description: 'For personal expenses without collateral',
  },
  {
    id: 'vehicle',
    name: 'Vehicle',
    icon: '🚗',
    defaultRate: 9,
    defaultTenureYears: 7,
    minAmount: 100000,
    maxAmount: 10000000,
    description: 'For purchasing car, bike, or other vehicles',
  },
  {
    id: 'home',
    name: 'Home Loan',
    icon: '🏠',
    defaultRate: 8.5,
    defaultTenureYears: 20,
    minAmount: 500000,
    maxAmount: 50000000,
    description: 'For purchasing or constructing your dream home',
  },
];

export const otherLoanPresets: LoanPreset[] = [
  {
    id: 'education',
    name: 'Education Loan',
    icon: '🎓',
    defaultRate: 10,
    defaultTenureYears: 10,
    minAmount: 100000,
    maxAmount: 7500000,
    description: 'For higher education and skill development',
  },
  {
    id: 'gold',
    name: 'Gold Loan',
    icon: '🥇',
    defaultRate: 11,
    defaultTenureYears: 3,
    minAmount: 5000,
    maxAmount: 5000000,
    description: 'Loan against gold jewelry or ornaments',
  },
  {
    id: 'business',
    name: 'Business Loan',
    icon: '💼',
    defaultRate: 14,
    defaultTenureYears: 7,
    minAmount: 100000,
    maxAmount: 20000000,
    description: 'For business expansion and working capital',
  },
  {
    id: 'lap',
    name: 'Loan Against Property',
    icon: '🏢',
    defaultRate: 9.5,
    defaultTenureYears: 15,
    minAmount: 500000,
    maxAmount: 50000000,
    description: 'Secured loan using property as collateral',
  },
  {
    id: 'creditcard',
    name: 'Credit Card EMI',
    icon: '💳',
    defaultRate: 15,
    defaultTenureYears: 2,
    minAmount: 1000,
    maxAmount: 500000,
    description: 'Convert credit card dues to EMI',
  },
  {
    id: 'custom',
    name: 'Custom',
    icon: '⚙️',
    defaultRate: 10,
    defaultTenureYears: 5,
    minAmount: 1000,
    maxAmount: 100000000,
    description: 'Configure your own loan parameters',
  },
];
